import { useState, useRef, useEffect, useId } from "react";
import { Input, Button } from "antd";
import { useTranslation } from "react-i18next";
import type { ValueChangedPropsOptional } from "@type/util";
import { Guide } from "./Guide";
import { TestId } from "@shared/testIds";

const TAG_OPTIONS = ["Front", "Back", "Left", "Right", "Top", "Bottom"] as const;

export interface ImageTagSelectorProps extends ValueChangedPropsOptional<string> {
  disabled?: boolean;
}

const TagPill = ({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) => (
  <button
    type="button"
    className={`px-[10px] py-[6px] rounded-full border border-solid text-[14px] font-medium leading-[16px] cursor-pointer transition-colors ${
      selected
        ? "bg-kds-accent-soft border-kds-accent-border text-kds-accent-fg"
        : "bg-kds-bg-element border-kds-border-solid text-kds-fg-soft hover:border-kds-accent-border hover:text-kds-accent-fg"
    }`}
    onClick={onClick}
  >
    {label}
  </button>
);

export const ImageTagPopoverContent = ({
  currentValue,
  onSave,
  maxLength = 20,
}: {
  currentValue: string;
  onSave: (value: string) => void;
  maxLength?: number;
}) => {
  const id = useId();
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(currentValue);
  const [selectedTag, setSelectedTag] = useState<string | null>(() => {
    const matched = TAG_OPTIONS.find((tag) => tag.toLowerCase() === currentValue.toLowerCase());
    return matched ?? null;
  });
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<any>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const validate = (val: string): string | null => {
    const trimmed = val.trim();
    if (!trimmed) return t("This field is required");
    if (trimmed.length > maxLength) return t("Tag must be {{max}} characters or fewer", { max: maxLength });
    return null;
  };

  const handleTagClick = (tag: string) => {
    const translatedTag = t(tag);
    setSelectedTag(tag);
    setInputValue(`${inputValue}${translatedTag}`);
    setError(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    if (error) setError(validate(val));
    const matched = TAG_OPTIONS.find((tag) => t(tag).toLowerCase() === val.toLowerCase());
    setSelectedTag(matched ?? null);
  };

  const handleSave = () => {
    const err = validate(inputValue);
    if (err) {
      setError(err);
      return;
    }
    onSave(inputValue.trim());
  };

  return (
    <div className="flex flex-col gap-[8px] w-[544px]">
      <div className="p-[12px] flex flex-col gap-[8px]">
        <div className="flex flex-col gap-[4px]">
          <Input
            id={id}
            ref={inputRef}
            data-testid={TestId.VideoImageCaptionInput}
            value={inputValue}
            onChange={handleInputChange}
            onPressEnter={handleSave}
            placeholder={t("Caption")}
            status={error ? "error" : undefined}
            className={`bg-kds-bg-element rounded-[8px] h-[32px] px-[12px] text-kds-fg leading-[16px] border border-solid ${
              error ? "border-kds-danger!" : "border-kds-border-solid!"
            }`}
          />
          {error && <span className="text-kds-danger-fg text-[12px] leading-[16px]">{error}</span>}
        </div>
        <div className="flex flex-wrap gap-[8px]">
          {TAG_OPTIONS.map((tag) => (
            <TagPill key={tag} label={t(tag)} selected={selectedTag === tag} onClick={() => handleTagClick(tag)} />
          ))}
        </div>
      </div>
      <div className="flex justify-between items-center border-t border-kds-border p-[16px]">
        <Guide />
        <Button data-testid={TestId.VideoImageCaptionSave} type="primary" size="middle" onClick={handleSave}>
          {t("Save")}
        </Button>
      </div>
    </div>
  );
};
